import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../../context/AnalyticsContext';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  RefreshCw, 
  ExternalLink,
  Settings,
  Loader2,
  Info,
  XCircle
} from 'lucide-react';

interface OAuthFlowTrackerProps {
  provider: 'zoho' | 'powerbi';
  onRetry?: () => void;
  onConfigure?: () => void;
}

type FlowStep = 'configuration' | 'oauth-init' | 'oauth-pending' | 'oauth-callback' | 'complete' | 'error';

interface FlowStepInfo {
  id: FlowStep;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'complete' | 'error';
  duration?: number;
}

const OAuthFlowTracker: React.FC<OAuthFlowTrackerProps> = ({ 
  provider, 
  onRetry, 
  onConfigure 
}) => {
  const { state, actions } = useAnalytics();
  const [currentStep, setCurrentStep] = useState<FlowStep>('configuration');
  const [stepTimer, setStepTimer] = useState(0);
  const [flowError, setFlowError] = useState<string | null>(null);

  const providerState = state.providers[provider];
  const config = state.configuration?.[provider];

  useEffect(() => {
    // Determine current step based on provider state
    if (flowError) {
      setCurrentStep('error');
    } else if (providerState.authorized && providerState.connected) {
      setCurrentStep('complete');
    } else if (providerState.loading) {
      setCurrentStep('oauth-pending');
    } else if (config?.enabled && providerState.configured) {
      setCurrentStep('oauth-init');
    } else {
      setCurrentStep('configuration');
    }
  }, [providerState, config, flowError]);

  useEffect(() => {
    // Timer for tracking step duration
    const interval = setInterval(() => {
      if (currentStep === 'oauth-pending' || currentStep === 'oauth-init') {
        setStepTimer(prev => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [currentStep]);

  useEffect(() => {
    // Reset timer when step changes
    setStepTimer(0);
  }, [currentStep]);

  useEffect(() => {
    // Monitor for errors
    if (providerState.error) {
      setFlowError(providerState.error);
    }
  }, [providerState.error]);

  const steps: FlowStepInfo[] = [
    {
      id: 'configuration',
      title: 'Configuration',
      description: `Configure ${provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} credentials and settings`,
      status: currentStep === 'configuration' ? 'active' : 
              config?.enabled ? 'complete' : 'pending'
    },
    {
      id: 'oauth-init',
      title: 'OAuth Initialization',
      description: 'Preparing OAuth authorization URL',
      status: currentStep === 'oauth-init' ? 'active' :
              currentStep === 'configuration' ? 'pending' : 'complete'
    },
    {
      id: 'oauth-pending',
      title: 'User Authorization',
      description: `Waiting for authorization in ${provider === 'zoho' ? 'Zoho' : 'Microsoft'} account`,
      status: currentStep === 'oauth-pending' ? 'active' :
              ['configuration', 'oauth-init'].includes(currentStep) ? 'pending' : 'complete',
      duration: stepTimer
    },
    {
      id: 'oauth-callback',
      title: 'Processing Authorization',
      description: 'Processing OAuth callback and storing credentials',
      status: currentStep === 'oauth-callback' ? 'active' :
              ['configuration', 'oauth-init', 'oauth-pending'].includes(currentStep) ? 'pending' : 'complete'
    },
    {
      id: 'complete',
      title: 'Setup Complete',
      description: 'Analytics provider successfully connected',
      status: currentStep === 'complete' ? 'complete' : 'pending'
    }
  ];

  const getStepIcon = (step: FlowStepInfo) => {
    switch (step.status) {
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'active':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStepBgColor = (step: FlowStepInfo) => {
    switch (step.status) {
      case 'complete':
        return 'bg-green-50 border-green-200';
      case 'active':
        return 'bg-blue-50 border-blue-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleRetryFlow = () => {
    setFlowError(null);
    setStepTimer(0);
    if (onRetry) {
      onRetry();
    }
  };

  const renderCurrentStepDetails = () => {
    switch (currentStep) {
      case 'configuration':
        return (
          <Card className="p-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-start space-x-3">
              <Settings className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-yellow-800">Configuration Required</h4>
                <p className="text-yellow-700 text-sm mt-1">
                  Please configure your {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} credentials 
                  before starting the OAuth flow.
                </p>
                <div className="mt-3">
                  <Button onClick={onConfigure} size="sm">
                    <Settings className="w-4 h-4 mr-2" />
                    Configure Now
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );

      case 'oauth-pending':
        return (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start space-x-3">
              <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5 animate-spin" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-800">Waiting for Authorization</h4>
                <p className="text-blue-700 text-sm mt-1">
                  Please complete the authorization in your {provider === 'zoho' ? 'Zoho' : 'Microsoft'} account.
                  This window will automatically update when authorization is complete.
                </p>
                <div className="mt-2 text-sm text-blue-600">
                  Time elapsed: {formatDuration(stepTimer)}
                </div>
                {stepTimer > 120 && (
                  <div className="mt-3">
                    <Button onClick={handleRetryFlow} size="sm" variant="outline">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry Authorization
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        );

      case 'complete':
        return (
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-green-800">Setup Complete!</h4>
                <p className="text-green-700 text-sm mt-1">
                  Your {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} integration is now active.
                  You can start discovering schemas and using analytics features.
                </p>
                <div className="mt-3 space-x-2">
                  <Button onClick={() => actions.getCachedSchema(provider)} size="sm">
                    Discover Schema
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/analytics'} 
                    size="sm" 
                    variant="outline"
                  >
                    Go to Analytics
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );

      case 'error':
        return (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-red-800">Setup Failed</h4>
                <p className="text-red-700 text-sm mt-1">
                  {flowError || 'An error occurred during the OAuth setup process.'}
                </p>
                <div className="mt-3 space-x-2">
                  <Button onClick={handleRetryFlow} size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry Setup
                  </Button>
                  <Button onClick={onConfigure} size="sm" variant="outline">
                    <Settings className="w-4 h-4 mr-2" />
                    Reconfigure
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">OAuth Setup Progress</h3>
          <p className="text-text-secondary text-sm">
            Track the progress of your {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} integration setup.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            currentStep === 'complete' ? 'bg-green-100 text-green-700' :
            currentStep === 'error' ? 'bg-red-100 text-red-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {currentStep === 'complete' ? 'Complete' :
             currentStep === 'error' ? 'Failed' :
             'In Progress'}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`p-4 rounded-lg border transition-colors ${getStepBgColor(step)}`}
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {getStepIcon(step)}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-text-primary">{step.title}</h4>
                  {step.duration !== undefined && step.status === 'active' && (
                    <span className="text-sm text-blue-600">
                      {formatDuration(step.duration)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-text-secondary mt-1">{step.description}</p>
              </div>
            </div>

            {/* Progress connector */}
            {index < steps.length - 1 && (
              <div className="ml-2.5 mt-3">
                <div className={`w-0.5 h-4 ${
                  step.status === 'complete' ? 'bg-green-300' : 'bg-gray-300'
                }`} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Current Step Details */}
      {renderCurrentStepDetails()}

      {/* Debug Information (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <Card className="p-4 bg-gray-50">
          <h4 className="font-medium text-gray-800 mb-2">Debug Information</h4>
          <div className="text-xs text-gray-600 space-y-1">
            <div>Current Step: {currentStep}</div>
            <div>Provider State: {JSON.stringify(providerState, null, 2)}</div>
            <div>Config Enabled: {config?.enabled ? 'Yes' : 'No'}</div>
            <div>Step Timer: {stepTimer}s</div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default OAuthFlowTracker;